import React from 'react';
import { getUserFirstName } from '../../utils/userUtils';

/**
 * Adaptive WelcomeBanner that changes based on dashboard stage.
 * Stages: empty, first_story, active, mature
 */
const WelcomeBanner = ({
  stage,
  showCelebration,
  metrics,
  greeting
}) => {
  const userName = getUserFirstName();

  // Empty state - new user, no content yet
  if (stage === 'empty') {
    return (
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b-4 border-orange-400 mb-6 sm:mb-8 rounded-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center gap-4">
            <div className="text-4xl sm:text-5xl">👋</div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#556B2F] mb-2">
                Welcome to Your Family Legacy, {userName}!
              </h1>
              <p className="text-[#666666] text-base sm:text-lg">
                Let's begin preserving your stories and wisdom for future generations.
              </p>
              <p className="text-[#666666] text-sm sm:text-base mt-1">
                Start by sharing your first memory, story, or family photo below.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // First story celebration - just created their first story
  if (showCelebration && stage === 'first_story') {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-4 border-green-500 mb-6 sm:mb-8 rounded-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-4xl sm:text-5xl">🎉</div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#556B2F] mb-2">
                  Great Start, {userName}!
                </h1>
                <p className="text-[#666666] text-base sm:text-lg">
                  You've shared your first story! Your family legacy is beginning to take shape.
                </p>
              </div>
            </div>
            <div className="hidden sm:flex bg-green-500 text-white rounded-full w-16 h-16 sm:w-20 sm:h-20 flex-col items-center justify-center">
              <div className="text-xs sm:text-sm font-bold">FIRST</div>
              <div className="text-xs sm:text-sm font-bold">STORY</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active user - building momentum with multiple stories
  if (stage === 'active') {
    return (
      <div className="bg-gradient-to-r from-green-50 to-teal-50 border-b-4 border-[#6B8E23] mb-6 sm:mb-8 rounded-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-4xl sm:text-5xl">🏆</div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#556B2F] mb-2">
                  You're Building Something Special!
                </h1>
                <p className="text-[#666666] text-base sm:text-lg">
                  {metrics.storiesCount} stories shared
                  {metrics.photosCount > 0 && ` • ${metrics.photosCount} photos uploaded`}
                  {metrics.timeCapsulesCount > 0 && ` • ${metrics.timeCapsulesCount} time capsule${metrics.timeCapsulesCount !== 1 ? 's' : ''}`}
                  {' • Keep it going!'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mature user - established with lots of content
  if (stage === 'mature') {
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b-4 border-yellow-500 mb-6 sm:mb-8 rounded-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {metrics.consecutiveDaysStreak >= 7 ? (
                <div className="relative">
                  <div className="text-4xl sm:text-5xl">🔥</div>
                  <div className="absolute -top-1 -right-1 bg-yellow-400 text-gray-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    {metrics.consecutiveDaysStreak}
                  </div>
                </div>
              ) : (
                <div className="text-4xl sm:text-5xl">✨</div>
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#556B2F] mb-2">
                  Welcome back, {userName}!
                  {metrics.consecutiveDaysStreak >= 7 && <span className="ml-2">🌟</span>}
                </h1>
                <p className="text-[#666666] text-base sm:text-lg">
                  {metrics.consecutiveDaysStreak >= 7 && `${metrics.consecutiveDaysStreak}-day streak • `}
                  {metrics.storiesCount} stories shared
                  {metrics.familyMembersCount > 0 && ` • ${metrics.familyMembersCount} family members connected`}
                </p>
                {metrics.totalStoryViews > 0 && (
                  <p className="text-[#666666] text-sm">
                    Your family has viewed your stories {metrics.totalStoryViews} times this week
                  </p>
                )}
              </div>
            </div>

            {/* Quick stats boxes for mature users */}
            <div className="hidden lg:flex gap-3">
              <StatBox value={metrics.storiesCount} label="Stories" color="green" />
              <StatBox value={metrics.photosCount} label="Photos" color="orange" />
              <StatBox value={metrics.timeCapsulesCount} label="Capsules" color="purple" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback - standard greeting
  return (
    <div className="mb-6 sm:mb-8">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl text-[#556B2F] mb-2">Your Family Legacy Dashboard</h1>
      <p className="text-[#666666] text-base sm:text-lg">
        {greeting}, {userName}. Let's continue building your family's story.
      </p>
    </div>
  );
};

// Small stat box component for mature users
const StatBox = ({ value, label, color }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-800',
    orange: 'bg-orange-100 text-orange-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className={`${colorClasses[color]} rounded-lg px-3 sm:px-4 py-2 sm:py-3 min-w-[70px] sm:min-w-[80px] text-center`}>
      <div className="text-xl sm:text-2xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
};

export default WelcomeBanner;
